import React from 'react';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import { NotificationsActive, CheckCircle } from '@mui/icons-material';
import type { Notification } from './types';
import NotificationItem from './NotificationItem';

interface NotificationPanelProps {
  notifications: Notification[];
  allRead: boolean;
  unreadCount: number;
  onMarkAsRead: (id: string) => void;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  allRead,
  unreadCount,
  onMarkAsRead,
  onClose,
}) => {
  const total = notifications.length;
  const readCount = total - unreadCount;
  const progress = total > 0 ? (readCount / total) * 100 : 0;

  return (
    /* Backdrop — блокирует весь экран */
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      {/* Панель */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 560,
          mx: 2,
          bgcolor: '#111113',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* Шапка */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239,68,68,0.25)',
                color: '#f87171',
                flexShrink: 0,
              }}
            >
              <NotificationsActive sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#f9fafb', fontWeight: 700, lineHeight: 1.2, fontSize: '1.05rem' }}>
                Уведомления
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                {unreadCount > 0
                  ? `Требуется ознакомление: ${unreadCount}`
                  : 'Все уведомления прочитаны'}
              </Typography>
            </Box>
          </Box>

          {/* Прогресс-бар */}
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 5,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.07)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: allRead
                    ? 'linear-gradient(90deg, #4ade80, #22c55e)'
                    : 'linear-gradient(90deg, #3b82f6, #6366f1)',
                },
              }}
            />
          </Box>
        </Box>

        {/* Список уведомлений */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
          }}
        >
          {notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} onMarkAsRead={onMarkAsRead} />
          ))}
        </Box>

        {/* Подвал */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {!allRead ? (
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Ознакомьтесь с каждым уведомлением и отметьте его прочитанным, чтобы продолжить работу.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4ade80' }}>
                <CheckCircle sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: '#4ade80', fontWeight: 600 }}>
                  Все уведомления прочитаны
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#4ade80',
                  color: '#111113',
                  px: 3,
                  borderRadius: '8px',
                  boxShadow: '0 0 20px rgba(74,222,128,0.3)',
                  '&:hover': {
                    bgcolor: '#22c55e',
                    boxShadow: '0 0 24px rgba(74,222,128,0.5)',
                  },
                }}
              >
                Продолжить работу
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationPanel;
